'use client'

import clsx from 'clsx'
import React, { useMemo } from 'react'

import { HoverPrefetchLink } from '@/_components/nextjs/hover-prefetch-link'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@/_components/ui/accordion'
import { IAmbulanceCategory } from '@/_types'

interface IProps {
	ambulanceCategory: IAmbulanceCategory
	searchValue?: string
	initialColumns?: number
}

interface ISubcategoryProps {
	subcategory: any
	parentCategoryId: number
	initialExpanded?: boolean
	columns?: number
}

// Helper function to normalize text for search (remove punctuation and convert to lowercase)
const normalizeText = (text: string): string => {
	return text
		.toLowerCase()
		.normalize('NFD') // Decompose characters with diacritics
		.replace(/[\u0300-\u036f]/g, '') // Remove diacritics
		.replace(/[^\w\s]/g, '') // Remove all punctuation
		.replace(/\s+/g, ' ') // Normalize whitespace
		.trim()
}

// Helper function to recursively filter items based on search value
const filterItems = (items: any[], searchValue: string): any[] => {
	if (!searchValue) return items

	const normalizedSearch = normalizeText(searchValue)

	return items
		.map(item => {
			const itemNameMatches = normalizeText(item.name).includes(normalizedSearch)
			const ambulanceNameMatches =
				item.ambulance && normalizeText(item.ambulance.name).includes(normalizedSearch)

			// Check if this item or its ambulance matches
			const currentItemMatches = itemNameMatches || ambulanceNameMatches

			// Recursively filter children
			const filteredChildren =
				item.children && item.children.length > 0
					? filterItems(item.children, searchValue)
					: []

			// Include item if it matches OR if it has matching children
			if (currentItemMatches || filteredChildren.length > 0) {
				return {
					...item,
					children: filteredChildren
				}
			}

			return null
		})
		.filter(Boolean)
}

// Helper function to split array into N columns while preserving order
const splitIntoColumns = (items: any[], columnCount: number): any[][] => {
	if (columnCount <= 0 || items.length === 0) return [items]

	const itemsPerColumn = Math.ceil(items.length / columnCount)
	const columns: any[][] = []

	for (let i = 0; i < columnCount; i++) {
		const start = i * itemsPerColumn
		const end = start + itemsPerColumn
		const columnItems = items.slice(start, end)

		if (columnItems.length > 0) {
			columns.push(columnItems)
		}
	}

	return columns
}

// Recursive component for rendering nested items
const RecursiveItem: React.FC<{ item: any }> = ({ item }) => {
	const hasChildren = item.children && item.children.length > 0
	const isSubpage = item.isSubpage

	if (!hasChildren) {
		return (
			<li
				className={clsx(
					'!my-0 !py-1 !px-2 border-b md:last:border-b-0 border-[rgba(0,0,0,0.2)] md:border-none',
					isSubpage && 'bg-[#a4e2d9] lg:rounded-xl'
				)}
			>
				<HoverPrefetchLink
					href={`/ambulancie/${item.ambulance.slug}`}
					className='!my-0 !py-[6px] gap-x-2 flex justify-between items-center group text-base font-normal hover:font-medium transition-all duration-300'
				>
					<span>{item.name}</span>
					<span className='w-[28px] h-[28px] items-center justify-center bg-white rounded-full flex md:opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0'>
						<svg className='sico w-[10px] h-[10px]'>
							<use href='/assets/layout/icons.svg#s_ar2_r' />
						</svg>
					</span>
				</HoverPrefetchLink>
			</li>
		)
	}

	return (
		<li className='w-full !my-0 !py-1'>
			<Accordion type='single' collapsible suppressHydrationWarning={true}>
				<AccordionItem value='item' className='border-none'>
					<AccordionTrigger className='hover:no-underline pt-0 [&>svg]:mr-4'>
						<div className='flex items-center w-full mx-4'>
							<span>{item.name}</span>
						</div>
					</AccordionTrigger>
					<AccordionContent>
						<ul className='ml-2 md:ml-5 space-y-1'>
							{item.children.map((child: any, index: number) => (
								<RecursiveItem key={index} item={child} />
							))}
						</ul>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</li>
	)
}

const SubcategorySection: React.FC<ISubcategoryProps> = ({ subcategory, columns = 3 }) => {
	const hasChildren = subcategory.children && subcategory.children.length > 0
	const hasAmbulance = subcategory.ambulance

	// If no children and has ambulance, show direct link
	if (!hasChildren && hasAmbulance) {
		// TODO: Show direct link to ambulance
		return null
	}

	// Render expandable subcategory with columns
	if (hasChildren) {
		// Split children into columns for desktop
		const columnData = splitIntoColumns(subcategory.children, columns)

		// Determine grid columns class based on columns prop (responsive)
		const getGridColsClass = () => {
			const lgClass =
				columns === 1
					? 'lg:grid-cols-1'
					: columns === 2
						? 'lg:grid-cols-2'
						: columns === 3
							? 'lg:grid-cols-3'
							: 'lg:grid-cols-4'
			return `grid-cols-1 md:grid-cols-2 ${lgClass}`
		}

		return (
			<div
				className={clsx(
					'bg-white/40 rounded-xl backdrop-grayscale-150 md:rounded-none md:bg-transparent md:backdrop-filter-none',
					'px-4.5 py-6 mb-0 lg:p-0 mb-4 lg:mb-10'
				)}
			>
				<p className='font-medium text-xl mb-4 ml-1'>{subcategory.name}</p>

				<div className={clsx('grid md:gap-4', getGridColsClass())}>
					{columnData.map((columnItems, columnIndex) => (
						<div
							key={columnIndex}
							className={clsx(
								columnIndex !== columnData.length - 1 &&
									'border-r border-r-transparent md:border-[rgba(0,0,0,0.2)] last:border-r-0'
							)}
						>
							<ul className='pr-4'>
								{columnItems.map((item: any, itemIndex: number) => (
									<RecursiveItem key={itemIndex} item={item} />
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
		)
	}

	return null
}

export default function AAmbulanceCategoryList({
	ambulanceCategory,
	searchValue = '',
	initialColumns = 3
}: IProps) {
	// Filter subcategories and their children based on search value
	// Use useMemo to avoid recalculating filters on every render
	const filteredSubcategories = useMemo(
		() => filterItems(ambulanceCategory.children || [], searchValue),
		[ambulanceCategory.children, searchValue]
	)

	const areAllSubcategoriesEmpty = useMemo(() => {
		return filteredSubcategories.every(subcategory => subcategory.children.length === 0)
	}, [filteredSubcategories])

	if (areAllSubcategoriesEmpty) {
		return (
			<div className='text-center text-current pb-4'>
				Nenašli ste ambulanciu, ktorú hľadáte - kontaktujte prosím naše Klientské centrum
				emailom na{' '}
				<a href='mailto:recepcia@medante.sk' className='underline'>
					recepcia@medante.sk
				</a>{' '}
				alebo telefonicky{' '}
				<a href='tel:+421220302303' className='underline'>
					+421 2 20 302 303
				</a>
			</div>
		)
	}

	return (
		<div className='space-y-4 lg:mt-6'>
			{filteredSubcategories.map((subcategory: any) => (
				<SubcategorySection
					key={subcategory.id}
					subcategory={subcategory}
					parentCategoryId={ambulanceCategory.id}
					columns={initialColumns}
					initialExpanded={true}
				/>
			))}
		</div>
	)
}
